import { ArrowSquareOut, GithubLogo, List, X } from "@phosphor-icons/react";
import type { ReactNode } from "react";
import { text } from "../i18n.js";
import {
  COMMUNITY_REPOSITORY,
  localeHref,
  switchLocaleHref,
  type Route,
} from "../lib/routes.js";
import type { Locale } from "../types.js";
import { BrandMark } from "./BrandMark.js";

interface ShellProps {
  readonly locale: Locale;
  readonly route: Route;
  readonly children: ReactNode;
}

function Navigation({ locale, route }: Omit<ShellProps, "children">) {
  const t = text(locale);
  const links = [
    [t.nav.themes, localeHref(locale)],
    [t.nav.install, localeHref(locale, "install")],
    [t.nav.submit, localeHref(locale, "submit")],
    [t.nav.report, localeHref(locale, "report")],
  ] as const;
  return (
    <>
      <nav className="desktop-nav" aria-label={locale === "zh-CN" ? "主导航" : "Primary navigation"}>
        {links.map(([label, href]) => <a href={href} key={href}>{label}</a>)}
      </nav>
      <div className="header-actions">
        <a className="language-link" href={switchLocaleHref(route, locale === "zh-CN" ? "en" : "zh-CN")}>
          {locale === "zh-CN" ? "EN" : "中文"}
        </a>
        <a className="github-link" href={COMMUNITY_REPOSITORY} target="_blank" rel="noreferrer">
          <GithubLogo aria-hidden="true" weight="fill" />
          <span>{t.nav.github}</span>
          <ArrowSquareOut aria-hidden="true" />
        </a>
      </div>
      <details className="mobile-menu">
        <summary><List aria-hidden="true" /><span>{t.menu}</span></summary>
        <div className="mobile-menu-panel">
          <div className="mobile-menu-heading"><strong>OpenChatGPTSkin</strong><X aria-hidden="true" /></div>
          <nav aria-label={locale === "zh-CN" ? "移动导航" : "Mobile navigation"}>
            {links.map(([label, href]) => <a href={href} key={href}>{label}</a>)}
            <a href={switchLocaleHref(route, locale === "zh-CN" ? "en" : "zh-CN")}>
              {locale === "zh-CN" ? "English" : "中文"}
            </a>
            <a href={COMMUNITY_REPOSITORY} target="_blank" rel="noreferrer">GitHub <ArrowSquareOut aria-hidden="true" /></a>
          </nav>
        </div>
      </details>
    </>
  );
}

export function Shell({ locale, route, children }: ShellProps) {
  const t = text(locale);
  return (
    <>
      <a className="skip-link" href="#main-content">{t.skip}</a>
      <header className="site-header">
        <div className="site-header-inner">
          <a className="brand" href={localeHref(locale)} aria-label="OpenChatGPTSkin Community">
            <BrandMark />
            <span><strong>OpenChatGPTSkin</strong><small>Community</small></span>
          </a>
          <Navigation locale={locale} route={route} />
        </div>
      </header>
      <main id="main-content">{children}</main>
      <footer className="site-footer">
        <div className="container footer-grid">
          <div>
            <a className="brand footer-brand" href={localeHref(locale)}><BrandMark /><span><strong>OpenChatGPTSkin</strong><small>Community</small></span></a>
            <p>{t.footerStatement}</p>
            <p className="footer-privacy">{t.footerPrivacy}</p>
          </div>
          <nav aria-label={locale === "zh-CN" ? "页脚导航" : "Footer navigation"}>
            <a href={localeHref(locale)}>{t.nav.themes}</a>
            <a href={localeHref(locale, "install")}>{t.nav.install}</a>
            <a href={localeHref(locale, "submit")}>{t.nav.submit}</a>
            <a href={localeHref(locale, "report")}>{t.nav.report}</a>
            <a href={`${COMMUNITY_REPOSITORY}/blob/main/LICENSE`}>MIT License</a>
          </nav>
        </div>
      </footer>
    </>
  );
}
